import { useContext, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import config from '@/config';
import ProfileDataContext from '@/context/ProfileDataContext';
import useCache from '@/hooks/useCache';

function hasContent(value) {
  if (!value) return false;

  if (typeof value === 'number') {
    return value > 0;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === 'object') {
    if ('count' in value) return Boolean(value.count);
    if ('total' in value) return Boolean(value.total);
    if ('length' in value && typeof value.length === 'number') {
      return value.length > 0;
    }

    return Object.keys(value).length > 0;
  }

  return Boolean(value);
}

export default function ProfileRedirect() {
  const { profileData } = useContext(ProfileDataContext);
  const fetchUrl = `${config.apis.api.url}/user/userProfile/${profileData._id}`;

  const query = { user_type: profileData.user_type };

  const { fetchData: userProfileInfo, loading } = useCache(`${profileData._id}_userProfileInfo`, fetchUrl, { includeSearchQueries: query });
  const isGallery = profileData.user_type === "gallery";
  const isArtist = profileData.user_type === "artist";
  const location = useLocation();

  const destination = useMemo(() => {
    if (!userProfileInfo) return null;

    const data = userProfileInfo.data;
    const hasArtworks = hasContent(data.artworks);
    const hasCollections = hasContent(data.collections);
    const hasPosts = hasContent(data.posts);
    const hasArtlists = hasContent(data.artlists);
    const hasArtists = hasContent(data.artists);
    const hasThoughts = hasContent(data.thoughts);
    const hasRatings = hasContent(data.ratings);
    const hasCommunity = hasThoughts || hasRatings;

    if (isGallery) {
      if (hasArtworks) return "artworks";
      if (hasPosts) return "board";
      if (hasArtlists) return "artlists";
      if (hasArtists) return "artists";
      return null;
    }
    if (isArtist) {
      if (hasArtworks) return "artworks";
      if (hasCollections) return "collection";
      if (hasCommunity) return "community";
      if (hasArtlists) return "artlists";
      if (hasPosts) return "board";
      return null;
    }

    // Collector
    if (hasCollections) return "collection";
    if (hasCommunity) return "community";
    if (hasArtlists) return "artlists";
    return null;
  }, [isArtist, isGallery, userProfileInfo]);

  if (loading) {
    return <></>;
  }

  if (destination) {
    return <Navigate to={destination} replace state={location.state} />;
  }

  return <></>;
}
